import { BankStatement, BankTransaction } from './camt053';
import { Cents, formatCents } from '../money';

/**
 * Mehrere Kontoauszugsdateien zu einer Liste zusammenfuehren.
 *
 * Das Haspa-Onlinebanking liefert die Umsatzinformation (camt.052) je TAG als
 * eigene Datei; ein Monat sind rund zwanzig Dateien. Der Import nimmt sie
 * zusammen entgegen und zeigt eine gemeinsame Tabelle.
 *
 * Derselbe Umsatz kann in zwei Dateien stehen (der heutige Tag zweimal
 * heruntergeladen, ein Kontoauszug und die Tagesdatei desselben Tags). Er
 * erscheint nur einmal: der Schluessel ist die bankTxId, dieselbe, an der die
 * Datenbank eine Doppelbuchung verhindert. Auch ohne diesen Schritt koennte
 * nichts doppelt gebucht werden — aber zwei gleiche Zeilen in der Tabelle
 * verfuehren dazu, beide anzuhaken, und die zweite endet mit einer
 * Fehlermeldung.
 *
 * Abgelehnt wird eine Auswahl ueber mehrere Konten oder ueber beide Formate:
 * Konten, weil der Abgleich sonst Zahlungen eines fremden Kontos anboete;
 * Formate, weil nicht belegt ist, dass camt.052 und camt.053 fuer denselben
 * Umsatz dieselbe Bankreferenz tragen — gemischt koennte derselbe Eingang
 * zweimal angeboten und gebucht werden.
 */

export interface StatementFile {
    name: string;
    statement: BankStatement;
}

export interface MergedStatements {
    format: BankStatement['format'];
    iban?: string;
    fileNames: string[];
    /** Auszugsnummern aller Dateien (nur bei camt.053 aussagekraeftig). */
    statementIds: string[];
    /** Nach Wertstellung sortiert, innerhalb eines Tags in Dateireihenfolge. */
    transactions: BankTransaction[];
    firstValueDate?: string;
    lastValueDate?: string;
    /** Umsaetze, die in einer zweiten Datei noch einmal standen. */
    duplicatesSkipped: number;
    /** Endsaldo, nur bei genau einer Datei — ueber mehrere Tage waere er missverstaendlich. */
    closingBalanceCents?: Cents;
    /**
     * Hinweise ohne Abbruch — die enthaltenen Umsaetze sind trotzdem echt:
     *   • Dateien, deren Salden nicht aufgehen (Anfangssaldo + gebuchte
     *     Umsaetze ergibt nicht den Endsaldo): meist ist der Tag noch nicht
     *     abgeschlossen oder die Datei unvollstaendig;
     *   • derselbe Umsatz mit abweichendem Betrag in zwei Dateien;
     *   • Umsaetze ohne Bankreferenz (Schluessel aus dem Inhalt gerechnet) —
     *     fuer sie greift der Doppelbuchungsschutz nur eingeschraenkt.
     */
    warnings: string[];
}

/** Schluessel aus dem Inhalt gerechnet (camt053.ts), keine Bankreferenz. */
const isComputedKey = (bankTxId: string): boolean => bankTxId.startsWith('camt-');

const balanceWarning = (file: StatementFile): string | null => {
    const { openingBalanceCents, closingBalanceCents, transactions } = file.statement;
    if (openingBalanceCents === undefined || closingBalanceCents === undefined) return null;
    // Der Endsaldo (CLBD) ist der GEBUCHTE Stand — vorgemerkte Umsaetze
    // stecken nicht darin.
    const bookedSum = transactions
        .filter((tx) => tx.booked)
        .reduce((sum, tx) => sum + tx.amountCents, 0);
    const difference = closingBalanceCents - (openingBalanceCents + bookedSum);
    if (difference === 0) return null;
    return `${file.name}: Anfangssaldo und Umsätze ergeben nicht den Endsaldo (Differenz ${formatCents(
        difference,
    )}) — die Datei ist vermutlich unvollständig.`;
};

/** Wirft bei einer Auswahl ueber mehrere Konten oder Formate. */
export const mergeStatements = (files: readonly StatementFile[]): MergedStatements => {
    if (!files.length) {
        throw new Error('Es wurde keine Datei eingelesen.');
    }

    const formats = Array.from(new Set(files.map((file) => file.statement.format)));
    if (formats.length > 1) {
        throw new Error(
            'Die Auswahl mischt Umsatzliste (camt.052) und Kontoauszug (camt.053). Bitte bei einem Format bleiben — gemischt erkennt der Import bereits gebuchte Umsätze nicht sicher.',
        );
    }
    // Bei mehreren Dateien muss jede ihr Konto nennen: eine Datei ohne IBAN
    // fiele sonst aus der Pruefung heraus und liefe unter dem Konto der
    // anderen mit (Codex Runde 4).
    if (files.length > 1) {
        const withoutAccount = files.filter((file) => !file.statement.iban);
        if (withoutAccount.length) {
            throw new Error(
                `Diese Dateien nennen kein Konto: ${withoutAccount
                    .map((file) => file.name)
                    .join(', ')}. Bitte einzeln importieren.`,
            );
        }
    }
    const ibans = Array.from(
        new Set(files.map((file) => file.statement.iban).filter((iban): iban is string => !!iban)),
    );
    if (ibans.length > 1) {
        throw new Error(
            `Die Dateien gehören zu verschiedenen Konten (${ibans.join(', ')}). Bitte je Konto einzeln importieren.`,
        );
    }

    const byKey = new Map<string, { tx: BankTransaction; order: number }>();
    let duplicatesSkipped = 0;
    const conflicts: string[] = [];
    const collected: Array<{ tx: BankTransaction; order: number }> = [];
    for (const file of files) {
        for (const tx of file.statement.transactions) {
            const known = byKey.get(tx.bankTxId);
            if (!known) {
                const entry = { tx, order: collected.length };
                byKey.set(tx.bankTxId, entry);
                collected.push(entry);
                continue;
            }
            duplicatesSkipped += 1;
            if (known.tx.amountCents !== tx.amountCents) {
                conflicts.push(
                    `${file.name}: derselbe Umsatz (${tx.bankTxId}) steht in einer anderen Datei mit ${formatCents(
                        known.tx.amountCents,
                    )} statt ${formatCents(tx.amountCents)} — bitte am Kontoauszug prüfen.`,
                );
            }
            // Vorgemerkt in der frueheren, gebucht in der spaeteren Datei:
            // der gebuchte Stand gilt. Sonst fehlte die Zahlung als buchbar,
            // obwohl sie laengst gebucht ist.
            if (!known.tx.booked && tx.booked) known.tx = tx;
        }
    }
    collected.sort((a, b) =>
        a.tx.valueDate === b.tx.valueDate
            ? a.order - b.order
            : a.tx.valueDate < b.tx.valueDate
              ? -1
              : 1,
    );
    const transactions = collected.map((entry) => entry.tx);

    return {
        format: formats[0],
        iban: ibans[0],
        fileNames: files.map((file) => file.name),
        statementIds: files.flatMap((file) => file.statement.statementIds).filter(Boolean),
        transactions,
        firstValueDate: transactions[0]?.valueDate,
        lastValueDate: transactions[transactions.length - 1]?.valueDate,
        duplicatesSkipped,
        closingBalanceCents: files.length === 1 ? files[0].statement.closingBalanceCents : undefined,
        warnings: [
            ...files.map(balanceWarning).filter((warning): warning is string => warning !== null),
            ...conflicts,
            ...(() => {
                const computed = transactions.filter((tx) => isComputedKey(tx.bankTxId)).length;
                return computed > 0
                    ? [
                          `${computed} Umsatz/Umsätze ohne Bankreferenz: für sie erkennt der Import eine Doppelbuchung nur, wenn die Datei unverändert erneut kommt — bitte vor dem Buchen prüfen.`,
                      ]
                    : [];
            })(),
        ],
    };
};
